import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { isBirthCheckOk } from 'validators/Validator';
import { FormErrors } from 'validators/ProfileValidator';
import {
	UpdateUserReqData,
	callUpdateUserApi,
	User,
	callCheckMailAddressDuplicateApi,
	callCheckGakkenIdDuplicateApi,
	UsersDisplayItems,
} from 'requests/RequestUser';
import { UserForm } from 'components/templates/ProfileEditTemplate';
import { formatDate, DATE } from 'common/dateFormatter';
import { RoutePath } from 'consts/RoutePath';
import { HttpStatus } from 'consts/HttpStatus';
import { ReactError } from 'consts/ReactError';

/**
 * ユーザプロフィール更新処理のカスタムフック
 * フォームバリデーション -> 更新実処理 -> ページ遷移 を段階的に分けている
 * useStateが非同期なのでuseEffectを使って段階的にしないとエラーをキャッチすることができないため
 */
export const useUpdateUserProfile = (
	formErrors: FormErrors,
	setFormErrors: React.Dispatch<React.SetStateAction<FormErrors>>,
	getErrorMessage: Function,
	actionError: string,
	setActionError: React.Dispatch<React.SetStateAction<string>>,
	userFormData: UserForm,
	prevUserData: User,
	handleCompleteDialogOpen: Function,
	setProcessLoading: React.Dispatch<React.SetStateAction<boolean>>,
	requiredDisplayItem?: UsersDisplayItems,
) => {
	const [isGidChange, setIsGidChange] = useState(false);
	// フォームバリデーション
	const [isFormValidationFinished, setIsFormValidationFinished] = useState(false);
	const update = async () => {
		setProcessLoading(true);

		Object.keys(formErrors).forEach((k) => {
			setFormErrors((prev) => ({ ...prev, [k]: getErrorMessage(k, userFormData[k], requiredDisplayItem) }));
		});

		// 年月日どれかが入力されている場合、生年月日の正当性チェック
		if (userFormData.birthYear || userFormData.birthMonth || userFormData.birthDate) {
			const isOk = isBirthCheckOk(userFormData.birthYear, userFormData.birthMonth, userFormData.birthDate);
			if (!isOk) {
				setFormErrors((prev) => ({ ...prev, birthYear: '正しい生年月日を入力してください。' }));
			}
		}
		setIsFormValidationFinished(true);
	};

	// 学研ID重複確認
	const [isGidDuplicateValidationFinished, setIsGidDuplicateValidationFinished] = useState(false);
	useEffect(() => {
		if (!isFormValidationFinished) {
			return;
		}

		const isError = !!Object.values(formErrors).find((e) => e !== '') || actionError !== '';
		if (isError) {
			setIsFormValidationFinished(false);
			setProcessLoading(false);
			return;
		}

		const execute = async () => {
			setActionError('');
			if (prevUserData.gid !== userFormData.gid) {
				setIsGidChange(true);
				const checkGidResult = await callCheckGakkenIdDuplicateApi({
					gid: userFormData.gid,
				});
				if (checkGidResult.status !== HttpStatus.OK) {
					setActionError(ReactError.E9999);
					history.push(RoutePath.ERROR);
					return;
				}
				if (checkGidResult.data.exist) {
					setFormErrors((prev) => ({ ...prev, gid: ReactError.E0005 }));
				}
			} else {
				setIsGidChange(false);
			}
			setIsGidDuplicateValidationFinished(true);
		};
		execute();
	}, [isFormValidationFinished]);

	// メールアドレス確認
	const [isCheckEmailFinished, setIsCheckEmailFinished] = useState(false);
	const history = useHistory();
	useEffect(() => {
		if (!isGidDuplicateValidationFinished) {
			return;
		}

		const isError = !!Object.values(formErrors).find((e) => e !== '') || actionError !== '';
		if (isError) {
			setIsFormValidationFinished(false);
			setIsGidDuplicateValidationFinished(false);
			setProcessLoading(false);
			return;
		}

		const execute = async () => {
			// メールアドレスに変更があれば重複確認 & 遷移先変更
			if (prevUserData.mailaddress_1 !== userFormData.mailaddress_1) {
				const checkMailResult = await callCheckMailAddressDuplicateApi({
					mailaddress: userFormData.mailaddress_1,
				});
				if (checkMailResult.status !== HttpStatus.OK) {
					setActionError(ReactError.E9999);
					setIsCheckEmailFinished(true);
					history.push(RoutePath.ERROR);
					return;
				}
				if (checkMailResult.data.exist) {
					setFormErrors((prev) => ({ ...prev, mailaddress_1: '既に登録されているメールアドレスです。' }));
					setIsCheckEmailFinished(true);
					return;
				}
				// メールアドレス確認画面へ遷移
				const isBirthExist = !!userFormData.birthYear && !!userFormData.birthMonth && !!userFormData.birthDate;
				const updateUserReaData: UpdateUserReqData = {
					isGidChange,
					data: {
						gid: userFormData.gid,
						mailaddress_1: userFormData.mailaddress_1,
						lastname: userFormData.lastname,
						firstname: userFormData.firstname,
						kana_lastname: userFormData.kana_lastname,
						kana_firstname: userFormData.kana_firstname,
						birth: isBirthExist
							? formatDate(
									new Date(
										Number(userFormData.birthYear),
										Number(userFormData.birthMonth) - 1,
										Number(userFormData.birthDate),
									),
									DATE,
							  )
							: undefined,
						nickname: userFormData.nickname,
						sex: userFormData.sex,
						country: userFormData.country,
						zip: userFormData.zip,
						tel: userFormData.tel,
						pref_cd: userFormData.pref_cd,
						address_2: userFormData.address_2,
						address_3: userFormData.address_3,
						address_4: userFormData.address_4,
						family_type: userFormData.family_type,
						agree_flg: userFormData.agree_flg,
						data_util_agree_flg: userFormData.data_util_agree_flg,
					},
				};
				history.push(RoutePath.PROFILE_EDIT_MAILADDRESS, updateUserReaData);
				return;
			}
			setIsCheckEmailFinished(true);
		};
		execute();
	}, [isGidDuplicateValidationFinished]);

	// 更新実処理
	const [isUpdateFinished, setIsUpdateFinished] = useState(false);
	useEffect(() => {
		if (!isCheckEmailFinished) {
			return;
		}

		const isError = !!Object.values(formErrors).find((e) => e !== '') || actionError !== '';
		if (isError) {
			setIsFormValidationFinished(false);
			setIsGidDuplicateValidationFinished(false);
			setIsCheckEmailFinished(false);
			setProcessLoading(false);
			return;
		}

		const executeUpdate = async () => {
			const isBirthExist = !!userFormData.birthYear && !!userFormData.birthMonth && !!userFormData.birthDate;
			const updateUserReaData: UpdateUserReqData = {
				isGidChange,
				data: {
					gid: userFormData.gid,
					mailaddress_1: userFormData.mailaddress_1,
					lastname: userFormData.lastname,
					firstname: userFormData.firstname,
					kana_lastname: userFormData.kana_lastname,
					kana_firstname: userFormData.kana_firstname,
					birth: isBirthExist
						? formatDate(
								new Date(
									Number(userFormData.birthYear),
									Number(userFormData.birthMonth) - 1,
									Number(userFormData.birthDate),
								),
								DATE,
						  )
						: undefined,
					nickname: userFormData.nickname,
					sex: userFormData.sex,
					country: userFormData.country,
					zip: userFormData.zip,
					tel: userFormData.tel,
					pref_cd: userFormData.pref_cd,
					address_2: userFormData.address_2,
					address_3: userFormData.address_3,
					address_4: userFormData.address_4,
					family_type: userFormData.family_type,
					agree_flg: userFormData.agree_flg,
					data_util_agree_flg: userFormData.data_util_agree_flg,
				},
			};

			const result = await callUpdateUserApi(updateUserReaData);
			if (result.status !== HttpStatus.OK) {
				setActionError(ReactError.E9999);
				setIsUpdateFinished(true);
				history.push(RoutePath.ERROR);
				return;
			}
			setIsUpdateFinished(true);
		};
		executeUpdate();
	}, [isCheckEmailFinished]);

	// ページ遷移
	useEffect(() => {
		if (!isUpdateFinished) {
			return;
		}

		const isError = actionError !== '';
		if (isError) {
			setIsFormValidationFinished(false);
			setIsGidDuplicateValidationFinished(false);
			setIsCheckEmailFinished(false);
			setIsUpdateFinished(false);
			setProcessLoading(false);
			return;
		}

		handleCompleteDialogOpen();
	}, [isUpdateFinished]);

	return {
		update,
	};
};
